import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import QrCodeScannerOutlinedIcon from "@mui/icons-material/QrCodeScannerOutlined";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import {
  pageContentCardSx,
  pageHeroShellSx,
} from "../../styles/pageModernStyles";
import {
  meetingActionBtnSx,
  meetingCaptureCardSx,
  meetingChipRowSx,
  meetingDesktopAsideSx,
  meetingDesktopMainSx,
  meetingDesktopSplitSx,
  meetingHeaderIconBtnSx,
  meetingHeaderToggleSx,
  meetingHeaderToolGroupLabelSx,
  meetingHeaderToolGroupSx,
  meetingHeaderToolIconBtnSx,
  meetingHeaderToolbarSx,
  meetingPageShellSx,
  meetingSectionGap,
  meetingStickyHeaderSx,
} from "./meetingUiStyles";
import {
  claimMeetingRecording,
  createMeetingFreeNote,
  deleteMeetingFreeNote,
  deleteMeetingSession,
  deleteMeetingPhoto,
  getMeetingSession,
  meetingMediaUrl,
  moveMeetingPhotos,
  patchMeetingFreeNote,
  patchMeetingPhoto,
  patchMeetingSession,
  releaseMeetingRecording,
  stopMeetingSession,
  summarizeMeetingSession,
  uploadMeetingAudioChunk,
} from "../../api/Meetings/meetingSessionsApi";
import { socket } from "../../socket";
import MeetingFreeNotesPanel from "./MeetingFreeNotesPanel";
import {
  countUploadingMedia,
  enqueueMeetingPhotoUpload,
  mergePhotosWithMediaUploads,
  subscribeMeetingMediaUploads,
  waitForSessionMediaUploads,
} from "./meetingMediaUploadQueue";
import {
  formatElapsed,
  canDeleteMeetingFromToken,
  formatMeetingMediaMaxMb,
  formatMeetingMediaWarnMb,
  hasMeetingConsent,
  isMeetingConsentComplete,
  isMeetingMediaOversize,
  shouldWarnMeetingMediaSize,
  mediaDaysLeftFromSession,
  MEETING_MEDIA_RETENTION_DAYS,
  notesByPhotoId,
  audioCouplingByPhotoId,
  photoOffsetFromStart,
  statusLabelKey,
  toDateInputValue,
  pickMeetingSummaryLocale,
  buildMeetingSampleNotes,
  sanitizeMeetingImportPhotoIds,
  canCreateSampleFromSelection,
} from "../../utils/meetingRecorder";
import { useMeetingRecorder } from "./useMeetingRecorder";
import MeetingBrandSelect from "./MeetingBrandSelect";
import MeetingAttendeeSelect from "./MeetingAttendeeSelect";
import MeetingClientContactSelect from "./MeetingClientContactSelect";
import MeetingDeleteDialog from "./MeetingDeleteDialog";
import MeetingConsentPanel from "./MeetingConsentPanel";
import MeetingSummaryPrint, {
  printMeetingSummary,
} from "./MeetingSummaryPrint";
import MeetingSummarizeConfirmDialog from "./MeetingSummarizeConfirmDialog";
import MeetingMediaGroupsFeed from "./MeetingMediaGroupsFeed";
import MeetingCreateSampleDialog from "./MeetingCreateSampleDialog";
import MeetingCreateMoodboardDialog from "./MeetingCreateMoodboardDialog";
import MeetingLinkMuestraDialog from "./MeetingLinkMuestraDialog";
import MeetingLeaveSamplesDialog from "./MeetingLeaveSamplesDialog";
import MeetingMoveMediaDialog from "./MeetingMoveMediaDialog";
import MeetingMediaGalleryDialog from "./MeetingMediaGalleryDialog";
import { downloadMeetingLeaveSamplesPdf } from "./meetingLeaveSamplesPdf";
import DrawerMuestraV2 from "../Muestras/Drawer/DrawerMuestraV2";
import {
  getMuestrasTipo,
  getMuestrasEstado,
  getTallas,
  getColoresMuestras,
  getProveedores,
  getMarcas as getMuestraMarcas,
  getEscandalloGrupos,
  getMuestraById,
  importMeetingMediaToMuestra,
} from "../../api/Muestras/muestraApi";
import { jwtDecode } from "jwt-decode";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";

const statusColor = {
  recording: "warning",
  processing: "info",
  ready: "success",
  failed: "error",
};

const EMPTY_MEDIA_GROUPS = [];

export default function MeetingSessionPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { contentMargin } = useContext(SidebarContext);
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const audioPausedForVideoRef = useRef(false);

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [captionOpen, setCaptionOpen] = useState(false);
  const [caption, setCaption] = useState("");
  const [captionPhotoId, setCaptionPhotoId] = useState(null);
  const [customerDraft, setCustomerDraft] = useState("");
  const [dateDraft, setDateDraft] = useState(() => toDateInputValue());
  const [attendeeIds, setAttendeeIds] = useState([]);
  const [clientContactIds, setClientContactIds] = useState([]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [consentDraft, setConsentDraft] = useState({
    consentClientsInformed: false,
    consentRecordingAccepted: false,
    consentAiAccepted: false,
  });
  const [savingConsent, setSavingConsent] = useState(false);
  const [mediaWarning, setMediaWarning] = useState("");
  const [mediaUploadingCount, setMediaUploadingCount] = useState(0);
  const [leaveUploadOpen, setLeaveUploadOpen] = useState(false);
  const [leavingWait, setLeavingWait] = useState(false);
  const [summarizing, setSummarizing] = useState(false);
  const [summarizeElapsedSec, setSummarizeElapsedSec] = useState(0);
  const [summarizeConfirmOpen, setSummarizeConfirmOpen] = useState(false);
  const [savingMediaGroups, setSavingMediaGroups] = useState(false);
  const [summaryLang, setSummaryLang] = useState("es");
  const [previewPhotoId, setPreviewPhotoId] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [highlightPhotoId, setHighlightPhotoId] = useState(null);
  const [expandedNoteIds, setExpandedNoteIds] = useState(() => new Set());
  const [productGateOpen, setProductGateOpen] = useState(false);
  const [pendingSamplePhotos, setPendingSamplePhotos] = useState([]);
  const [emptyTeamNoteWarnOpen, setEmptyTeamNoteWarnOpen] = useState(false);
  const [emptyTeamNotePhotoIds, setEmptyTeamNotePhotoIds] = useState([]);
  const [needGroupWarnOpen, setNeedGroupWarnOpen] = useState(false);
  const [linkMuestraOpen, setLinkMuestraOpen] = useState(false);
  const [linkingMuestra, setLinkingMuestra] = useState(false);
  const [leaveSamplesOpen, setLeaveSamplesOpen] = useState(false);
  const [leaveSamplesListOpen, setLeaveSamplesListOpen] = useState(false);
  const [leaveSamplesPdfBusy, setLeaveSamplesPdfBusy] = useState(false);
  const [muestraDrawerOpen, setMuestraDrawerOpen] = useState(false);
  const [newMuestraDraft, setNewMuestraDraft] = useState(null);
  const [selectedProductForMuestra, setSelectedProductForMuestra] =
    useState(null);
  const [pendingMeetingMedia, setPendingMeetingMedia] = useState(null);
  const [muestraCatalogs, setMuestraCatalogs] = useState(null);
  const [muestraEditMode, setMuestraEditMode] = useState(false);
  const [mediaSelectedCount, setMediaSelectedCount] = useState(0);
  const [noteSaving, setNoteSaving] = useState(false);
  const [moveMediaOpen, setMoveMediaOpen] = useState(false);
  const [movingMedia, setMovingMedia] = useState(false);
  const [moodboardOpen, setMoodboardOpen] = useState(false);
  const [moodboardPhotos, setMoodboardPhotos] = useState([]);
  const mediaFeedRef = useRef(null);
  const softRefreshBusyRef = useRef(false);
  const [floatingDockHost, setFloatingDockHost] = useState(null);
  const canDelete = useMemo(() => {
    try {
      return canDeleteMeetingFromToken(
        jwtDecode(localStorage.getItem("token") || ""),
      );
    } catch {
      return false;
    }
  }, []);
  const currentUserId = useMemo(() => {
    try {
      const decoded = jwtDecode(localStorage.getItem("token") || "");
      const n = Number(decoded?.userId ?? decoded?.id);
      return Number.isFinite(n) && n > 0 ? n : null;
    } catch {
      return null;
    }
  }, []);
  const userCanCreateSample = useMemo(() => {
    try {
      const decoded = jwtDecode(localStorage.getItem("token") || "");
      return Boolean(decoded?.PermisoCreateSample);
    } catch {
      return false;
    }
  }, []);

  const applySessionPayload = useCallback(
    (data) => {
      if (!data) return null;
      const photos = mergePhotosWithMediaUploads(data.photos, id);
      const next = { ...data, photos };
      setSession(next);
      setMediaUploadingCount(countUploadingMedia(id));
      setCustomerDraft(data.customerName || "");
      setDateDraft(toDateInputValue(data.meetingDate || data.startedAt));
      setAttendeeIds(
        (data.attendees || [])
          .map((a) => Number(a.userId))
          .filter((n) => Number.isFinite(n) && n > 0),
      );
      setClientContactIds(
        (data.clientContacts || [])
          .map((c) => Number(c.contactId))
          .filter((n) => Number.isFinite(n) && n > 0),
      );
      return next;
    },
    [id],
  );

  const load = useCallback(
    async (opts = {}) => {
      const quiet = Boolean(opts.quiet);
      const keepError = Boolean(opts.keepError);
      if (!quiet) setLoading(true);
      if (!keepError) setError("");
      try {
        const data = await getMeetingSession(id);
        return applySessionPayload(data);
      } catch (err) {
        setError(
          err.response?.data?.error || err.message || t("Meetings.loadError"),
        );
        return null;
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [applySessionPayload, id, t],
  );

  const softRefreshFromPeers = useCallback(async () => {
    if (softRefreshBusyRef.current) return;
    softRefreshBusyRef.current = true;
    try {
      const data = await getMeetingSession(id);
      if (!data) return;
      setSession((prev) => {
        const photos = mergePhotosWithMediaUploads(data.photos, id);
        if (!prev) return { ...data, photos };
        return {
          ...prev,
          ...data,
          photos,
          freeNotes: data.freeNotes || [],
          mediaGroups: data.mediaGroups,
          leftWithClient: data.leftWithClient,
          recordingByUserId: data.recordingByUserId ?? null,
          recordingByUsername: data.recordingByUsername || "",
          recordingStartedAt: data.recordingStartedAt || null,
          audioCount: data.audioCount,
          photoCount: data.photoCount,
          videoCount: data.videoCount,
          imageCount: data.imageCount,
          status: data.status,
          updatedAt: data.updatedAt,
        };
      });
      setMediaUploadingCount(countUploadingMedia(id));
    } catch {
      /* silencio: poll/socket de colaboración */
    } finally {
      softRefreshBusyRef.current = false;
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!id) return undefined;
    const meetingId = Number(id);
    socket.emit("join-meeting", { meetingSessionId: meetingId });
    const onUpdated = (payload) => {
      if (Number(payload?.meetingSessionId) !== meetingId) return;
      softRefreshFromPeers();
    };
    socket.on("meeting-updated", onUpdated);
    const poll = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      softRefreshFromPeers();
    }, 4000);
    const onVisible = () => {
      if (document.visibilityState === "visible") softRefreshFromPeers();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      socket.emit("leave-meeting", { meetingSessionId: meetingId });
      socket.off("meeting-updated", onUpdated);
      window.clearInterval(poll);
      document.removeEventListener("visibilitychange", onVisible);
      // Si este dispositivo tenía el mic, soltarlo al salir
      releaseMeetingRecording(meetingId).catch(() => {});
    };
  }, [id, softRefreshFromPeers]);

  useEffect(() => {
    setMediaUploadingCount(countUploadingMedia(id));
    return subscribeMeetingMediaUploads(() => {
      setMediaUploadingCount(countUploadingMedia(id));
      setSession((prev) => {
        if (!prev) return prev;
        const base = (prev.photos || []).filter((p) => !p.pendingUpload);
        return {
          ...prev,
          photos: mergePhotosWithMediaUploads(base, id),
        };
      });
    });
  }, [id]);

  const requestLeaveMeeting = useCallback(() => {
    if (countUploadingMedia(id) > 0) {
      setLeaveUploadOpen(true);
      return;
    }
    navigate("/reuniones");
  }, [id, navigate]);

  const leaveAfterUploads = useCallback(async () => {
    setLeavingWait(true);
    try {
      await waitForSessionMediaUploads(id);
    } finally {
      setLeavingWait(false);
      setLeaveUploadOpen(false);
      navigate("/reuniones");
    }
  }, [id, navigate]);

  const leaveAnyway = useCallback(() => {
    setLeaveUploadOpen(false);
    navigate("/reuniones");
  }, [navigate]);

  const onChunk = useCallback(
    async ({ file, seq, durationMs }) => {
      await uploadMeetingAudioChunk(id, file, { seq, durationMs });
      // Evitar GET completo: pisa el feed si hay una foto/vídeo subiendo a la vez.
      setSession((prev) =>
        prev
          ? {
              ...prev,
              audioCount: Math.max(
                Number(prev.audioCount) || 0,
                Number(seq) + 1,
              ),
            }
          : prev,
      );
    },
    [id],
  );

  const onError = useCallback(
    (err) => {
      setError(err?.message || t("Meetings.recordError"));
    },
    [t],
  );

  const recorder = useMeetingRecorder({ onChunk, onError });

  const handleDelete = async () => {
    setDeleting(true);
    setError("");
    try {
      await deleteMeetingSession(id);
      setDeleteOpen(false);
      navigate("/reuniones");
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || t("Meetings.deleteError"),
      );
    } finally {
      setDeleting(false);
    }
  };

  const recordingHeldByOther =
    session &&
    Number(session.recordingByUserId) > 0 &&
    Number(session.recordingByUserId) !== Number(currentUserId) &&
    !recorder.isRecording;
  const canRecord =
    session &&
    hasMeetingConsent(session) &&
    (session.status === "recording" || session.status === "failed") &&
    !recorder.isRecording &&
    !recordingHeldByOther;
  const canEndMeeting =
    session &&
    hasMeetingConsent(session) &&
    (session.status === "recording" || session.status === "failed");
  const canTakePhoto =
    session &&
    hasMeetingConsent(session) &&
    ["recording", "failed", "ready", "processing"].includes(session.status);
  const canSummarizePermission = session?.canSummarize !== false;
  const canSummarizeNow =
    session &&
    canSummarizePermission &&
    hasMeetingConsent(session) &&
    !recorder.isRecording &&
    !recorder.isPausedForMedia &&
    !summarizing &&
    ["recording", "failed", "ready"].includes(session.status) &&
    (session.audioCount > 0 || session.photoCount > 0);
  const summaryDone = session?.status === "ready" && !summarizing;
  const needsConsent = session && !hasMeetingConsent(session);

  useEffect(() => {
    if (!summarizing) {
      setSummarizeElapsedSec(0);
      return undefined;
    }
    const started = Date.now();
    const tick = () =>
      setSummarizeElapsedSec(Math.floor((Date.now() - started) / 1000));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [summarizing]);

  const handleSaveConsent = async () => {
    if (!isMeetingConsentComplete(consentDraft)) {
      setError(t("Meetings.consentRequired"));
      return;
    }
    setSavingConsent(true);
    setError("");
    try {
      const data = await patchMeetingSession(id, consentDraft);
      setSession(data);
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || t("Meetings.consentError"),
      );
    } finally {
      setSavingConsent(false);
    }
  };

  const handleStart = async () => {
    if (!hasMeetingConsent(session)) {
      setError(t("Meetings.consentRequired"));
      return;
    }
    setError("");
    setBusy(true);
    try {
      const claimed = await claimMeetingRecording(id);
      if (claimed) applySessionPayload(claimed);
      await recorder.start(session?.audioCount || 0);
    } catch (err) {
      try {
        await releaseMeetingRecording(id);
      } catch {
        /* ignore */
      }
      setError(
        err.response?.data?.error || err.message || t("Meetings.micDenied"),
      );
      await softRefreshFromPeers();
    } finally {
      setBusy(false);
    }
  };

  const handleStopAudio = async () => {
    setBusy(true);
    try {
      await recorder.stop();
      try {
        await releaseMeetingRecording(id);
      } catch {
        /* ignore */
      }
      await load({ quiet: true, keepError: true });
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || t("Meetings.stopError"),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleEndMeeting = async () => {
    setBusy(true);
    setError("");
    try {
      if (recorder.isRecording) {
        await recorder.stop();
      }
      try {
        await releaseMeetingRecording(id);
      } catch {
        /* ignore */
      }
      await stopMeetingSession(id);
      await load();
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.message ||
          t("Meetings.endMeetingError"),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleCreateFreeNote = async (text) => {
    setNoteSaving(true);
    setError("");
    try {
      const note = await createMeetingFreeNote(id, text);
      setSession((prev) => {
        if (!prev) return prev;
        const list = [...(prev.freeNotes || []), note];
        return { ...prev, freeNotes: list };
      });
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.message ||
          t("Meetings.freeNotesError"),
      );
      throw err;
    } finally {
      setNoteSaving(false);
    }
  };

  const handleUpdateFreeNote = async (note, text) => {
    if (!note?.id) return;
    setNoteSaving(true);
    setError("");
    try {
      const updated = await patchMeetingFreeNote(id, note.id, text);
      setSession((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          freeNotes: (prev.freeNotes || []).map((n) =>
            Number(n.id) === Number(note.id) ? { ...n, ...updated } : n,
          ),
        };
      });
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.message ||
          t("Meetings.freeNotesUpdateError"),
      );
      throw err;
    } finally {
      setNoteSaving(false);
    }
  };

  const handleDeleteFreeNote = async (note) => {
    if (!note?.id) return;
    setNoteSaving(true);
    setError("");
    try {
      await deleteMeetingFreeNote(id, note.id);
      setSession((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          freeNotes: (prev.freeNotes || []).filter(
            (n) => Number(n.id) !== Number(note.id),
          ),
        };
      });
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.message ||
          t("Meetings.freeNotesDeleteError"),
      );
    } finally {
      setNoteSaving(false);
    }
  };

  const mediaPickerCleanupRef = useRef(null);
  const mediaUploadInFlightRef = useRef(false);

  const clearMediaPickerListeners = useCallback(() => {
    if (typeof mediaPickerCleanupRef.current === "function") {
      mediaPickerCleanupRef.current();
      mediaPickerCleanupRef.current = null;
    }
  }, []);

  const resumeAudioIfPausedForVideo = useCallback(async () => {
    if (!audioPausedForVideoRef.current && !recorder.isPausedForMedia) return;
    audioPausedForVideoRef.current = false;
    try {
      await recorder.resumeAfterMediaCapture();
    } catch (err) {
      setError(
        err.message ||
          t("Meetings.micDenied") ||
          "No se pudo reanudar el audio",
      );
    }
  }, [recorder.isPausedForMedia, recorder.resumeAfterMediaCapture, t]);

  const uploadOneMediaFile = async (file) => {
    if (isMeetingMediaOversize(file.size)) {
      setError(
        t("Meetings.mediaHardLimit", { maxMb: formatMeetingMediaMaxMb() }),
      );
      setMediaWarning("");
      throw new Error("oversize");
    }
    if (shouldWarnMeetingMediaSize(file.size)) {
      setMediaWarning(
        t("Meetings.mediaHeavyWarn", {
          warnMb: formatMeetingMediaWarnMb(),
          sizeMb: Math.round(file.size / (1024 * 1024)),
        }),
      );
    }
    const offsetMs = photoOffsetFromStart(session.startedAt);
    const isVideoFile =
      String(file.type || "").startsWith("video/") ||
      /\.(mov|mp4|m4v|webm|avi)$/i.test(file.name || "");
    const localId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const localUrl = URL.createObjectURL(file);
    const pendingPhoto = {
      id: localId,
      fileUrl: localUrl,
      mimeType: file.type || (isVideoFile ? "video/mp4" : "image/jpeg"),
      kind: isVideoFile ? "video" : "photo",
      caption: "",
      teamNote: "",
      linkedMuestras: [],
      offsetMs,
      offsetLabel: "",
      pendingUpload: true,
    };
    const entry = enqueueMeetingPhotoUpload({
      sessionId: id,
      file,
      pendingPhoto,
      offsetMs,
      capturedAt: new Date().toISOString(),
    });
    setSession((prev) => {
      if (!prev) return prev;
      const base = (prev.photos || []).filter((p) => !p.pendingUpload);
      return {
        ...prev,
        photos: mergePhotosWithMediaUploads(base, id),
        photoCount: (prev.photoCount || 0) + 1,
        imageCount: isVideoFile
          ? (prev.imageCount ?? prev.photoCount ?? 0)
          : (prev.imageCount ?? prev.photoCount ?? 0) + 1,
        videoCount: isVideoFile
          ? (prev.videoCount || 0) + 1
          : prev.videoCount || 0,
      };
    });
    try {
      return await entry.promise;
    } catch (err) {
      setSession((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          photoCount: Math.max(0, (prev.photoCount || 1) - 1),
          imageCount: isVideoFile
            ? prev.imageCount
            : Math.max(0, (prev.imageCount || 1) - 1),
          videoCount: isVideoFile
            ? Math.max(0, (prev.videoCount || 1) - 1)
            : prev.videoCount || 0,
          photos: mergePhotosWithMediaUploads(
            (prev.photos || []).filter((p) => !p.pendingUpload),
            id,
          ),
        };
      });
      throw err;
    }
  };

  const handleMediaFile = async (event) => {
    clearMediaPickerListeners();
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length || !session) {
      await resumeAudioIfPausedForVideo();
      return;
    }
    if (!hasMeetingConsent(session)) {
      setError(t("Meetings.consentRequired"));
      await resumeAudioIfPausedForVideo();
      return;
    }
    mediaUploadInFlightRef.current = true;
    setError("");
    setMediaWarning("");
    // Encolar todas a la vez (la cola ya gestiona las peticiones).
    // No bloquear Foto/Vídeo/Galería con busy: con muchos vídeos el resumen
    // o la subida secuencial dejaba la reunión inutilizable.
    const uploadPromises = files.map((file) =>
      uploadOneMediaFile(file).catch((err) => {
        if (err?.message === "oversize") return { __oversize: true };
        setError(
          err.response?.data?.error || err.message || t("Meetings.photoError"),
        );
        return null;
      }),
    );
    // Liberar audio / picker enseguida; las subidas siguen en background.
    mediaUploadInFlightRef.current = false;
    await resumeAudioIfPausedForVideo();

    const results = await Promise.all(uploadPromises);
    const uploaded = results.filter((r) => r && !r.__oversize);
    if (uploaded.length) {
      const lastPhoto = uploaded[uploaded.length - 1];
      setCaptionPhotoId(lastPhoto.id);
      setCaption("");
      setCaptionOpen(files.length === 1 && uploaded.length === 1);
      load({ quiet: true, keepError: true });
    }
  };

  const openPhotoPicker = () => {
    if (!canTakePhoto) return;
    photoInputRef.current?.click();
  };

  const openGalleryPicker = () => {
    if (!canTakePhoto) return;
    galleryInputRef.current?.click();
  };

  const openVideoPicker = async () => {
    if (!canTakePhoto) return;
    clearMediaPickerListeners();
    let settled = false;
    const safetyTimer = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      clearMediaPickerListeners();
      resumeAudioIfPausedForVideo();
    }, 120000);
    try {
      if (recorder.isRecording && !recorder.isPausedForMedia) {
        setMediaWarning(t("Meetings.videoPausesAudio"));
        audioPausedForVideoRef.current = true;
        await recorder.pauseForMediaCapture();
      }
      // Si cancela el selector, al volver a la app reanudamos el audio.
      // No reanudar en el primer focus (algunos navegadores disparan focus al abrir el diálogo).
      let sawBlur = false;
      const onBlur = () => {
        sawBlur = true;
      };
      const onReturn = () => {
        if (!sawBlur) return;
        window.removeEventListener("focus", onReturn);
        document.removeEventListener("visibilitychange", onVisible);
        window.removeEventListener("blur", onBlur);
        window.setTimeout(() => {
          if (settled) return;
          if (mediaUploadInFlightRef.current) return;
          settled = true;
          window.clearTimeout(safetyTimer);
          resumeAudioIfPausedForVideo();
        }, 600);
      };
      const onVisible = () => {
        if (document.visibilityState === "visible") onReturn();
      };
      window.addEventListener("blur", onBlur);
      window.addEventListener("focus", onReturn);
      document.addEventListener("visibilitychange", onVisible);
      mediaPickerCleanupRef.current = () => {
        settled = true;
        window.clearTimeout(safetyTimer);
        window.removeEventListener("focus", onReturn);
        window.removeEventListener("blur", onBlur);
        document.removeEventListener("visibilitychange", onVisible);
      };
      videoInputRef.current?.click();
    } catch (err) {
      window.clearTimeout(safetyTimer);
      clearMediaPickerListeners();
      audioPausedForVideoRef.current = false;
      setError(err.message || t("Meetings.photoError"));
      try {
        await recorder.resumeAfterMediaCapture();
      } catch {
        /* ya parado */
      }
    }
  };

  const saveCaption = async () => {
    if (!captionPhotoId) {
      setCaptionOpen(false);
      return;
    }
    const text = String(caption || "").trim();
    try {
      if (text) {
        const updated = await patchMeetingPhoto(id, captionPhotoId, {
          teamNote: text,
        });
        setSession((prev) => {
          if (!prev?.photos) return prev;
          return {
            ...prev,
            photos: prev.photos.map((p) =>
              Number(p.id) === Number(captionPhotoId)
                ? { ...p, teamNote: updated.teamNote || text }
                : p,
            ),
          };
        });
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setCaptionOpen(false);
      setCaptionPhotoId(null);
      setCaption("");
    }
  };

  const requestSummarize = () => {
    if (summarizing || busy || !canSummarizeNow) return;
    // Confirmar siempre al regenerar; también en la 1ª si hay mucha media (cuesta OpenAI).
    const mediaTotal = (session?.photoCount || 0) + (session?.videoCount || 0);
    if (summaryDone || mediaTotal > 20) {
      setSummarizeConfirmOpen(true);
      return;
    }
    handleSummarize();
  };

  const handleSummarize = async () => {
    if (summarizing || busy) return;
    setSummarizeConfirmOpen(false);
    setSummarizing(true);
    setError("");
    setSession((prev) => (prev ? { ...prev, status: "processing" } : prev));
    try {
      if (customerDraft !== (session.customerName || "")) {
        await patchMeetingSession(id, { customerName: customerDraft });
      }
      const data = await summarizeMeetingSession(id);
      setSession(data);
    } catch (err) {
      const apiError = err.response?.data?.error || err.response?.data?.detail;
      const status = err.response?.status;
      const detail =
        apiError ||
        (status === 403 ? t("Meetings.summarizeForbidden") : null) ||
        err.message ||
        t("Meetings.summarizeError");
      setError(
        status && !apiError ? `${detail} (HTTP ${status})` : String(detail),
      );
      // Recarga sin pantallazo blanco ni borrar el error
      await load({ quiet: true, keepError: true });
    } finally {
      setSummarizing(false);
    }
  };

  const summary = session?.summary;
  const displaySummary = useMemo(() => {
    if (!summary) return null;
    const pack = pickMeetingSummaryLocale(summary, summaryLang);
    return { ...summary, ...pack };
  }, [summary, summaryLang]);
  const photos = session?.photos || [];
  const audioChunks = session?.audioChunks || [];
  const notesById = useMemo(
    () =>
      notesByPhotoId(
        displaySummary,
        summary?.transcript ||
          summary?.finalTranscript ||
          summary?.fullTranscript,
        {
          photos,
          // EN: el audio de fotos es Whisper en ES; usamos visualNotes traducidas.
          includePhotoVisualNotes: summaryLang === "en",
        },
      ),
    [displaySummary, summary, photos, summaryLang],
  );

  const handleSaveMediaGroups = useCallback(
    async (groups) => {
      setSavingMediaGroups(true);
      setError("");
      // Optimistic: la UI local manda; evita «necesitas grupo» antes de que responda el BACK
      setSession((prev) =>
        prev
          ? { ...prev, mediaGroups: Array.isArray(groups) ? groups : [] }
          : prev,
      );
      try {
        const data = await patchMeetingSession(id, { mediaGroups: groups });
        const serverGroups = Array.isArray(data?.mediaGroups)
          ? data.mediaGroups
          : [];
        setSession((prev) => {
          if (!prev && !data) return prev;
          const base = Array.isArray(data?.photos)
            ? data
            : { ...(prev || {}), ...(data || {}), photos: prev?.photos };
          const nextGroups =
            Array.isArray(groups) && groups.length && !serverGroups.length
              ? groups
              : serverGroups.length
                ? serverGroups
                : prev?.mediaGroups || [];
          return {
            ...base,
            photos: mergePhotosWithMediaUploads(base.photos || [], id),
            mediaGroups: nextGroups,
          };
        });
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      } finally {
        setSavingMediaGroups(false);
      }
    },
    [id],
  );

  const handleSaveTeamNote = useCallback(
    async (photoId, teamNote) => {
      setError("");
      try {
        const updated = await patchMeetingPhoto(id, photoId, { teamNote });
        setSession((prev) => {
          if (!prev?.photos) return prev;
          return {
            ...prev,
            photos: prev.photos.map((p) =>
              Number(p.id) === Number(photoId)
                ? { ...p, teamNote: updated.teamNote || "" }
                : p,
            ),
          };
        });
      } catch (err) {
        setError(err.response?.data?.error || err.message);
        throw err;
      }
    },
    [id],
  );

  const handleDeleteMedia = useCallback(
    async (photoId) => {
      setError("");
      // Optimistic: quitar solo esa pieza (el DELETE a veces devolvía sesión sin photos)
      setSession((prev) => {
        if (!prev?.photos) return prev;
        const photos = prev.photos.filter(
          (p) => Number(p.id) !== Number(photoId),
        );
        const removed = prev.photos.find(
          (p) => Number(p.id) === Number(photoId),
        );
        const wasVideo =
          removed &&
          (removed.kind === "video" ||
            String(removed.mimeType || "").startsWith("video/"));
        return {
          ...prev,
          photos,
          photoCount: Math.max(0, (prev.photoCount || 1) - 1),
          imageCount: wasVideo
            ? prev.imageCount
            : Math.max(0, (prev.imageCount || 1) - 1),
          videoCount: wasVideo
            ? Math.max(0, (prev.videoCount || 1) - 1)
            : prev.videoCount || 0,
          mediaGroups: (prev.mediaGroups || [])
            .map((g) => ({
              ...g,
              mediaIds: (g.mediaIds || []).filter(
                (mid) => Number(mid) !== Number(photoId),
              ),
            }))
            .filter(
              (g) =>
                (g.mediaIds || []).length > 0 || String(g.label || "").trim(),
            ),
        };
      });
      setPreviewPhotoId((prev) =>
        Number(prev) === Number(photoId) ? null : prev,
      );
      setCaptionPhotoId((prev) =>
        Number(prev) === Number(photoId) ? null : prev,
      );
      try {
        const data = await deleteMeetingPhoto(id, photoId);
        setSession((prev) => {
          if (Array.isArray(data?.photos)) {
            return {
              ...data,
              photos: mergePhotosWithMediaUploads(data.photos, id),
              mediaGroups: Array.isArray(data.mediaGroups)
                ? data.mediaGroups
                : prev?.mediaGroups || [],
            };
          }
          // No pisar el feed si el API no trae photos
          if (!prev) return data;
          return {
            ...prev,
            ...data,
            photos: prev.photos,
            mediaGroups: Array.isArray(data?.mediaGroups)
              ? data.mediaGroups
              : prev.mediaGroups,
          };
        });
      } catch (err) {
        await load({ quiet: true, keepError: true });
        setError(
          err.response?.data?.error ||
            err.message ||
            t("Meetings.deleteMediaError"),
        );
        throw err;
      }
    },
    [id, load, t],
  );

  const handleConfirmMoveMedia = useCallback(
    async (targetMeetingSessionId) => {
      const photoIds = mediaFeedRef.current?.getSelectedPhotoIds?.() || [];
      if (!photoIds.length || movingMedia) return;
      setMovingMedia(true);
      setError("");
      try {
        const data = await moveMeetingPhotos(id, {
          targetMeetingSessionId,
          photoIds,
        });
        setSession((prev) => {
          if (Array.isArray(data?.photos)) {
            return {
              ...data,
              photos: mergePhotosWithMediaUploads(data.photos, id),
              mediaGroups: Array.isArray(data.mediaGroups)
                ? data.mediaGroups
                : prev?.mediaGroups || [],
            };
          }
          return data || prev;
        });
        mediaFeedRef.current?.clearSelection?.();
        setMediaSelectedCount(0);
        setMoveMediaOpen(false);
      } catch (err) {
        setError(
          err.response?.data?.error ||
            err.message ||
            t("Meetings.moveMediaError"),
        );
      } finally {
        setMovingMedia(false);
      }
    },
    [id, movingMedia, t],
  );

  const ensureMuestraCatalogs = useCallback(async () => {
    if (muestraCatalogs) return muestraCatalogs;
    const [
      tiposM,
      estados,
      tallasData,
      coloresData,
      proveedoresData,
      marcasData,
      gruposEscandallo,
    ] = await Promise.all([
      getMuestrasTipo(),
      getMuestrasEstado(),
      getTallas(),
      getColoresMuestras(),
      getProveedores(),
      getMuestraMarcas(),
      getEscandalloGrupos(),
    ]);
    const next = {
      tiposMuestra: tiposM || [],
      tiposEstado: estados || [],
      tiposTallas: tallasData || [],
      tiposColores: coloresData || [],
      tiposProveedores: proveedoresData || [],
      tiposMarcas: marcasData || [],
      escandalloGrupos: gruposEscandallo || [],
    };
    setMuestraCatalogs(next);
    return next;
  }, [muestraCatalogs]);

  const openCreateSampleFlow = useCallback((selectedPhotos) => {
    const missing = selectedPhotos
      .filter((p) => !String(p?.teamNote || "").trim())
      .map((p) => Number(p.id))
      .filter((id) => Number.isFinite(id));
    if (missing.length) {
      setEmptyTeamNotePhotoIds(missing);
      setEmptyTeamNoteWarnOpen(true);
      return;
    }
    setEmptyTeamNotePhotoIds([]);
    setProductGateOpen(true);
  }, []);

  const handleCreateSampleFromMedia = useCallback(
    async (selectedPhotos) => {
      if (!selectedPhotos?.length) return;
      setPendingSamplePhotos(selectedPhotos);
      let groups = session?.mediaGroups || EMPTY_MEDIA_GROUPS;
      try {
        if (typeof mediaFeedRef.current?.flushSaveGroups === "function") {
          const flushed = await mediaFeedRef.current.flushSaveGroups();
          if (Array.isArray(flushed)) groups = flushed;
        } else if (typeof mediaFeedRef.current?.getMediaGroups === "function") {
          groups = mediaFeedRef.current.getMediaGroups() || groups;
        }
      } catch (_) {
        if (typeof mediaFeedRef.current?.getMediaGroups === "function") {
          groups = mediaFeedRef.current.getMediaGroups() || groups;
        }
      }
      const groupCheck = canCreateSampleFromSelection(
        selectedPhotos.map((p) => p.id),
        groups,
      );
      if (!groupCheck.ok) {
        setNeedGroupWarnOpen(true);
        return;
      }
      openCreateSampleFlow(selectedPhotos);
    },
    [openCreateSampleFlow, session?.mediaGroups],
  );

  const applyImportedMeetingLinks = useCallback((result) => {
    const copied = Array.isArray(result?.copied) ? result.copied : [];
    if (!copied.length) return;
    const byPhotoId = new Map(copied.map((c) => [Number(c.photoId), c]));
    setSession((prev) => {
      if (!prev?.photos?.length) return prev;
      let changed = false;
      const photosNext = prev.photos.map((p) => {
        const hit = byPhotoId.get(Number(p.id));
        if (!hit) return p;
        changed = true;
        const linked =
          Array.isArray(hit.linkedMuestras) && hit.linkedMuestras.length
            ? hit.linkedMuestras
            : (() => {
                const prevLinks =
                  Array.isArray(p.linkedMuestras) && p.linkedMuestras.length
                    ? p.linkedMuestras.slice()
                    : p.idMuestra
                      ? [
                          {
                            idMuestra: p.idMuestra,
                            muestraNombre: p.muestraNombre || "",
                          },
                        ]
                      : [];
                const mid = Number(hit.idMuestra);
                if (!Number.isFinite(mid) || mid <= 0) return prevLinks;
                const without = prevLinks.filter(
                  (x) => Number(x.idMuestra) !== mid,
                );
                without.push({
                  idMuestra: mid,
                  muestraNombre: hit.muestraNombre || p.muestraNombre || "",
                });
                return without;
              })();
        const last = linked[linked.length - 1];
        return {
          ...p,
          linkedMuestras: linked,
          idMuestra: last?.idMuestra ?? hit.idMuestra ?? p.idMuestra,
          muestraNombre:
            last?.muestraNombre || hit.muestraNombre || p.muestraNombre || "",
        };
      });
      return changed ? { ...prev, photos: photosNext } : prev;
    });
  }, []);

  const handleLinkSampleFromMedia = useCallback(
    async (selectedPhotos) => {
      if (!selectedPhotos?.length) return;
      setPendingSamplePhotos(selectedPhotos);
      let groups = session?.mediaGroups || EMPTY_MEDIA_GROUPS;
      try {
        if (typeof mediaFeedRef.current?.flushSaveGroups === "function") {
          const flushed = await mediaFeedRef.current.flushSaveGroups();
          if (Array.isArray(flushed)) groups = flushed;
        } else if (typeof mediaFeedRef.current?.getMediaGroups === "function") {
          groups = mediaFeedRef.current.getMediaGroups() || groups;
        }
      } catch (_) {
        if (typeof mediaFeedRef.current?.getMediaGroups === "function") {
          groups = mediaFeedRef.current.getMediaGroups() || groups;
        }
      }
      const groupCheck = canCreateSampleFromSelection(
        selectedPhotos.map((p) => p.id),
        groups,
      );
      if (!groupCheck.ok) {
        setNeedGroupWarnOpen(true);
        return;
      }
      setLinkMuestraOpen(true);
    },
    [session?.mediaGroups],
  );

  const confirmLinkExistingMuestra = useCallback(
    async (muestra) => {
      const muestraId = Number(muestra?.ID_MUESTRA ?? muestra?.id);
      const photoIds = sanitizeMeetingImportPhotoIds(
        pendingSamplePhotos.map((p) => p.id),
      );
      if (!muestraId || !photoIds.length) return;
      setLinkingMuestra(true);
      setError("");
      try {
        const result = await importMeetingMediaToMuestra(muestraId, {
          sessionId: Number(id),
          photoIds,
        });
        applyImportedMeetingLinks(result);
        setLinkMuestraOpen(false);
        setPendingSamplePhotos([]);
      } catch (err) {
        setError(
          err.response?.data?.error ||
            err.message ||
            t("Meetings.linkMuestraError"),
        );
      } finally {
        setLinkingMuestra(false);
      }
    },
    [applyImportedMeetingLinks, id, pendingSamplePhotos, t],
  );

  const continueCreateSampleWithoutNotes = useCallback(() => {
    setEmptyTeamNoteWarnOpen(false);
    setProductGateOpen(true);
  }, []);

  const cancelCreateSampleEmptyNotes = useCallback(() => {
    setEmptyTeamNoteWarnOpen(false);
    setEmptyTeamNotePhotoIds([]);
    setPendingSamplePhotos([]);
  }, []);

  const closeNeedGroupWarn = useCallback(() => {
    setNeedGroupWarnOpen(false);
    setPendingSamplePhotos([]);
  }, []);

  const handleProductChosenForSample = useCallback(
    async (product) => {
      setError("");
      try {
        await ensureMuestraCatalogs();
        const notes = buildMeetingSampleNotes(pendingSamplePhotos, {
          labels: {
            photo: t("Meetings.photo"),
            video: t("Meetings.video"),
          },
        });
        const photoIds = sanitizeMeetingImportPhotoIds(
          pendingSamplePhotos.map((p) => p.id),
        );
        setSelectedProductForMuestra({
          id: product.id,
          ID_MARCA: product.ID_MARCA,
          nombre: product.nombre,
        });
        setNewMuestraDraft({
          MUESTRA: `SAMP-${Math.random()
            .toString(36)
            .substring(2, 7)
            .toUpperCase()}`,
          ID_ESTADO: 1,
          ID_Marca: product.ID_MARCA || "",
          ID_TIPO_MUESTRA: "",
          ID_TALLA_BASE: "",
          ID_COLOR_BASE: "",
          FECHA_PETICION: null,
          FECHA_PREVISTA: null,
          FECHA_RECEPCION: null,
          NOTAS: notes,
          PROVEEDOR: "",
          ID_RESPONSABLE_SUPPLIER: null,
          PRIORIDAD: null,
          FECHA_PREVISTA_FABRICA: null,
          CALIDAD_NOMBRE: "",
          CALIDAD_ESTADO: null,
          PRINT_NOMBRE: "",
          PRINT_ESTADO: null,
          ID_PRODUCTO: product.id,
        });
        setPendingMeetingMedia({
          sessionId: Number(id),
          photoIds,
        });
        setMuestraEditMode(false);
        setMuestraDrawerOpen(true);
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      }
    },
    [ensureMuestraCatalogs, id, pendingSamplePhotos, t],
  );

  const closeMuestraDrawer = useCallback(() => {
    setMuestraDrawerOpen(false);
    setNewMuestraDraft(null);
    setSelectedProductForMuestra(null);
    setPendingMeetingMedia(null);
    setMuestraEditMode(false);
  }, []);

  const handleDeleteMuestraFromMeeting = useCallback((muestraId) => {
    const idNum = Number(muestraId);
    if (!Number.isFinite(idNum) || idNum <= 0) return;
    setSession((prev) => {
      if (!prev?.photos?.length) return prev;
      let changed = false;
      const photosNext = prev.photos.map((p) => {
        const linked =
          Array.isArray(p.linkedMuestras) && p.linkedMuestras.length
            ? p.linkedMuestras
            : p.idMuestra
              ? [
                  {
                    idMuestra: p.idMuestra,
                    muestraNombre: p.muestraNombre || "",
                  },
                ]
              : [];
        if (!linked.some((x) => Number(x.idMuestra) === idNum)) return p;
        changed = true;
        const next = linked.filter((x) => Number(x.idMuestra) !== idNum);
        const last = next[next.length - 1];
        return {
          ...p,
          linkedMuestras: next,
          idMuestra: last?.idMuestra ?? null,
          muestraNombre: last?.muestraNombre || "",
        };
      });
      return changed ? { ...prev, photos: photosNext } : prev;
    });
  }, []);

  const handleLeaveSamplesDone = useCallback(
    async (updatedSession) => {
      if (updatedSession) {
        setSession((prev) => {
          if (!prev) return updatedSession;
          return {
            ...prev,
            ...updatedSession,
            photos: Array.isArray(updatedSession.photos)
              ? updatedSession.photos
              : prev.photos,
            audioChunks: Array.isArray(updatedSession.audioChunks)
              ? updatedSession.audioChunks
              : prev.audioChunks,
            leftWithClient: Array.isArray(updatedSession.leftWithClient)
              ? updatedSession.leftWithClient
              : prev.leftWithClient || [],
          };
        });
      }
      try {
        await load({ quiet: true, keepError: true });
      } catch {
        /* ignore */
      }
    },
    [load],
  );

  const leftWithClient = useMemo(
    () =>
      Array.isArray(session?.leftWithClient) ? session.leftWithClient : [],
    [session?.leftWithClient],
  );

  const handleMuestraPersistedFromMeeting = useCallback(
    async (saved) => {
      if (!saved?.ID_MUESTRA) return;
      setMuestraEditMode(true);
      setNewMuestraDraft((prev) => ({
        ...(prev || {}),
        ...saved,
        ID_MUESTRA: saved.ID_MUESTRA,
      }));
      setPendingMeetingMedia(null);
      try {
        await load({ quiet: true, keepError: true });
      } catch {
        /* ignore */
      }
    },
    [load],
  );

  const openLinkedMuestra = useCallback(
    async (muestraId) => {
      if (!muestraId) return;
      setError("");
      try {
        await ensureMuestraCatalogs();
        const full = await getMuestraById(muestraId);
        setSelectedProductForMuestra({
          id: full.ID_PRODUCTO,
          ID_MARCA: full.ID_Marca,
          nombre: full.producto?.nombre || full.NombreCliente,
        });
        setNewMuestraDraft(full);
        setMuestraEditMode(true);
        setPendingMeetingMedia(null);
        setMuestraDrawerOpen(true);
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      }
    },
    [ensureMuestraCatalogs],
  );
  const audioByPhotoId = useMemo(
    () => audioCouplingByPhotoId(photos, audioChunks),
    [photos, audioChunks],
  );
  const previewPhoto = useMemo(
    () => photos.find((p) => Number(p.id) === Number(previewPhotoId)) || null,
    [photos, previewPhotoId],
  );
  const previewIsVideo =
    previewPhoto &&
    (previewPhoto.kind === "video" ||
      String(previewPhoto.mimeType || "")
        .toLowerCase()
        .startsWith("video/"));

  /** Clic en miniatura del feed: visor/reproductor de esa pieza (no la galería). */
  const openMedia = useCallback((photoId) => {
    const idNum = Number(photoId);
    if (!Number.isFinite(idNum)) return;
    setPreviewPhotoId(idNum);
    setPreviewOpen(true);
    setHighlightPhotoId(idNum);
  }, []);

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
    setPreviewPhotoId(null);
  }, []);

  const openGallery = useCallback(() => {
    setPreviewOpen(false);
    setPreviewPhotoId(null);
    setGalleryOpen(true);
  }, []);

  const closeGallery = useCallback(() => {
    setGalleryOpen(false);
  }, []);

  const jumpToPhotoInMeeting = useCallback((photoId) => {
    const idNum = Number(photoId);
    if (!Number.isFinite(idNum)) return;
    setGalleryOpen(false);
    setHighlightPhotoId(idNum);
    window.setTimeout(() => {
      mediaFeedRef.current?.revealPhoto?.(idNum);
      window.setTimeout(() => {
        const el = document.querySelector(`[data-meeting-photo-id="${idNum}"]`);
        if (el?.scrollIntoView) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    }, 140);
  }, []);

  const handleLeaveSamplesPdf = useCallback(async () => {
    if (!leftWithClient.length || leaveSamplesPdfBusy) return;
    setLeaveSamplesPdfBusy(true);
    setError("");
    try {
      await downloadMeetingLeaveSamplesPdf({
        session,
        leftWithClient,
        photos: session?.photos || photos || [],
      });
    } catch (err) {
      setError(err.message || t("Meetings.leaveSamplesPdfError"));
    } finally {
      setLeaveSamplesPdfBusy(false);
    }
  }, [leftWithClient, leaveSamplesPdfBusy, session, photos, t]);

  const elapsedLabel = useMemo(
    () => formatElapsed(recorder.elapsedMs),
    [recorder.elapsedMs],
  );

  if (loading) {
    return (
      <Box sx={meetingPageShellSx(contentMargin)}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (!session) {
    return (
      <Box sx={meetingPageShellSx(contentMargin)}>
        <Alert severity="error">{error || t("Meetings.loadError")}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate("/reuniones")}>
          {t("Meetings.back")}
        </Button>
      </Box>
    );
  }

  const timerClosed = Boolean(session.endedAt) || session.status === "ready";
  const timerLabel = recorder.isRecording
    ? elapsedLabel
    : timerClosed
      ? "—"
      : "00:00";
  const timerHint = recorder.isRecording
    ? t("Meetings.timerRecordingHint")
    : timerClosed
      ? t("Meetings.timerClosedHint")
      : t("Meetings.timerIdleHint");

  return (
    <Box sx={meetingPageShellSx(contentMargin)}>
      <Paper
        sx={{
          ...pageHeroShellSx(theme, { sticky: true }),
          ...meetingStickyHeaderSx(theme),
          overflow: "visible",
        }}
        elevation={0}
        className="meeting-no-print"
      >
        <Box sx={{ px: { xs: 1.25, sm: 1.5 }, pt: 1.25, pb: 1.35 }}>
          <Stack spacing={1} sx={{ minWidth: 0 }}>
            <Stack
              direction="row"
              alignItems="flex-start"
              spacing={0.75}
              sx={{ minWidth: 0 }}
            >
              <IconButton
                onClick={requestLeaveMeeting}
                size="small"
                sx={{ mt: 0.15 }}
              >
                <ArrowBackIosNewOutlinedIcon fontSize="small" />
              </IconButton>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    lineHeight: 1.25,
                    wordBreak: "break-word",
                  }}
                >
                  {session.title || t("Meetings.untitled")}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.35, lineHeight: 1.4 }}
                >
                  {[
                    session.meetingDate,
                    session.customerName || t("Meetings.noCustomer"),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </Typography>
              </Box>
              <Box
                sx={{
                  ...meetingHeaderToolbarSx,
                  justifyContent: "flex-end",
                  maxWidth: { xs: "72%", sm: "78%", md: "62%" },
                  flexShrink: 0,
                }}
              >
                {summary && summary.status !== "error" ? (
                  <Box sx={meetingHeaderToolGroupSx(theme)}>
                    <Typography
                      component="span"
                      variant="caption"
                      sx={meetingHeaderToolGroupLabelSx()}
                    >
                      {t("Meetings.toolGroupActa")}
                    </Typography>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={summaryLang}
                      onChange={(_, next) => {
                        if (next) setSummaryLang(next);
                      }}
                      sx={{
                        ...meetingHeaderToggleSx,
                        height: 30,
                        minHeight: 30,
                        "& .MuiToggleButton-root": {
                          ...meetingHeaderToggleSx["& .MuiToggleButton-root"],
                          height: 30,
                          minHeight: 30,
                          px: 1,
                        },
                      }}
                    >
                      <ToggleButton value="es" aria-label="ES">
                        ES
                      </ToggleButton>
                      <Tooltip
                        title={
                          summary?.en
                            ? t("Meetings.summaryLangEn")
                            : t("Meetings.summaryLangHint")
                        }
                      >
                        <span>
                          <ToggleButton
                            value="en"
                            aria-label="EN"
                            disabled={!summary?.en}
                          >
                            EN
                          </ToggleButton>
                        </span>
                      </Tooltip>
                    </ToggleButtonGroup>
                    <Tooltip title={t("Meetings.printExport")}>
                      <IconButton
                        size="small"
                        onClick={printMeetingSummary}
                        aria-label={t("Meetings.printExport")}
                        sx={meetingHeaderToolIconBtnSx}
                      >
                        <PrintOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ) : null}
                {photos.length ? (
                  <Box sx={meetingHeaderToolGroupSx(theme, { accent: true })}>
                    <Typography
                      component="span"
                      variant="caption"
                      sx={meetingHeaderToolGroupLabelSx(true)}
                    >
                      {t("Meetings.toolGroupMedia")}
                    </Typography>
                    {mediaSelectedCount ? (
                      <Chip
                        size="small"
                        color="primary"
                        label={mediaSelectedCount}
                        sx={{
                          height: 20,
                          minWidth: 22,
                          fontWeight: 800,
                          fontSize: "0.68rem",
                          "& .MuiChip-label": { px: 0.7 },
                        }}
                      />
                    ) : null}
                    <Tooltip title={t("Meetings.mediaGalleryOpen")}>
                      <IconButton
                        size="small"
                        onClick={openGallery}
                        aria-label={t("Meetings.mediaGalleryOpen")}
                        sx={meetingHeaderToolIconBtnSx}
                      >
                        <CollectionsOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip
                      title={
                        mediaSelectedCount
                          ? t("Meetings.mediaGroupAddFromSelection", {
                              count: mediaSelectedCount,
                            })
                          : t("Meetings.mediaGroupAdd")
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => mediaFeedRef.current?.addGroup?.()}
                          disabled={savingMediaGroups}
                          aria-label={t("Meetings.mediaGroupAdd")}
                          sx={meetingHeaderToolIconBtnSx}
                        >
                          <AddOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t("Meetings.linkMuestra")}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={!mediaSelectedCount || linkingMuestra}
                          onClick={() => mediaFeedRef.current?.linkSample?.()}
                          aria-label={t("Meetings.linkMuestra")}
                          sx={meetingHeaderToolIconBtnSx}
                        >
                          <LinkOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t("Meetings.createSample")}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={!mediaSelectedCount}
                          onClick={() => mediaFeedRef.current?.createSample?.()}
                          aria-label={t("Meetings.createSample")}
                          sx={{
                            ...meetingHeaderToolIconBtnSx,
                            color: mediaSelectedCount
                              ? "primary.main"
                              : undefined,
                          }}
                        >
                          <Inventory2OutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t("Meetings.createMoodboard")}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={!mediaSelectedCount}
                          onClick={() => {
                            const ids = new Set(
                              mediaFeedRef.current?.getSelectedPhotoIds?.() ||
                                [],
                            );
                            const selected = photos.filter((p) =>
                              ids.has(Number(p.id)),
                            );
                            setMoodboardPhotos(selected);
                            setMoodboardOpen(true);
                          }}
                          aria-label={t("Meetings.createMoodboard")}
                          sx={{
                            ...meetingHeaderToolIconBtnSx,
                            color: mediaSelectedCount
                              ? "secondary.main"
                              : undefined,
                          }}
                        >
                          <DashboardCustomizeOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t("Meetings.moveMedia")}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={!mediaSelectedCount || movingMedia}
                          onClick={() => setMoveMediaOpen(true)}
                          aria-label={t("Meetings.moveMedia")}
                          sx={meetingHeaderToolIconBtnSx}
                        >
                          <DriveFileMoveOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                ) : null}
                <Box sx={meetingHeaderToolGroupSx(theme)}>
                  <Typography
                    component="span"
                    variant="caption"
                    sx={meetingHeaderToolGroupLabelSx()}
                  >
                    {t("Meetings.toolGroupCliente")}
                  </Typography>
                  <Tooltip title={t("Meetings.leaveSamples")}>
                    <IconButton
                      size="small"
                      onClick={() => setLeaveSamplesOpen(true)}
                      aria-label={t("Meetings.leaveSamples")}
                      sx={meetingHeaderToolIconBtnSx}
                    >
                      <QrCodeScannerOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {leftWithClient.length ? (
                    <Tooltip title={t("Meetings.leaveSamplesPdf")}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={leaveSamplesPdfBusy}
                          onClick={handleLeaveSamplesPdf}
                          aria-label={t("Meetings.leaveSamplesPdf")}
                          sx={{
                            ...meetingHeaderToolIconBtnSx,
                            color: "info.main",
                          }}
                        >
                          {leaveSamplesPdfBusy ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <Badge
                              color="info"
                              badgeContent={leftWithClient.length}
                              overlap="circular"
                            >
                              <PictureAsPdfOutlinedIcon fontSize="small" />
                            </Badge>
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : null}
                </Box>
                {canDelete ? (
                  <Tooltip title={t("Meetings.delete")}>
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        aria-label={t("Meetings.delete")}
                        disabled={busy || recorder.isRecording || deleting}
                        onClick={() => setDeleteOpen(true)}
                        sx={meetingHeaderIconBtnSx}
                      >
                        <DeleteOutlineOutlinedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : null}
              </Box>
            </Stack>

            <Box sx={meetingChipRowSx}>
              <Chip
                size="small"
                color={statusColor[session.status] || "default"}
                label={t(statusLabelKey(session.status))}
              />
              {recorder.isRecording ? (
                <Chip
                  size="small"
                  color="success"
                  label={t("Meetings.audioRecordingOn")}
                />
              ) : ["recording", "failed"].includes(session.status) ? (
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  label={t("Meetings.audioRecordingOff")}
                />
              ) : null}
              {!session.mediaPurged ? (
                <Chip
                  size="small"
                  variant="outlined"
                  color="warning"
                  label={t("Meetings.mediaDaysLeftChip", {
                    days: mediaDaysLeftFromSession(session) ?? "—",
                  })}
                />
              ) : (
                <Chip
                  size="small"
                  variant="outlined"
                  label={t("Meetings.mediaPurgedChip")}
                />
              )}
              {leftWithClient.length ? (
                <Chip
                  size="small"
                  color="info"
                  clickable
                  onClick={() => setLeaveSamplesListOpen((v) => !v)}
                  icon={
                    <ExpandMoreOutlinedIcon
                      sx={{
                        ml: "4px !important",
                        transform: leaveSamplesListOpen
                          ? "rotate(180deg)"
                          : "none",
                        transition: "transform 160ms ease",
                      }}
                    />
                  }
                  label={t("Meetings.leaveSamplesChipCount", {
                    count: leftWithClient.length,
                  })}
                  sx={{ fontWeight: 700 }}
                />
              ) : null}
            </Box>
            <Collapse in={leaveSamplesListOpen && leftWithClient.length > 0}>
              <Box
                sx={{
                  mt: 0.75,
                  maxHeight: { xs: 96, sm: 120 },
                  overflowX: "auto",
                  overflowY: "auto",
                  WebkitOverflowScrolling: "touch",
                  pb: 0.25,
                }}
              >
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  useFlexGap
                  spacing={0.75}
                  sx={{ minWidth: "min-content" }}
                >
                  {leftWithClient.map((item) => (
                    <Chip
                      key={item.idMuestra}
                      size="small"
                      color="info"
                      variant="outlined"
                      label={t("Meetings.leaveSamplesChip", {
                        name: item.muestraNombre || `#${item.idMuestra}`,
                      })}
                      onClick={() => {
                        const mid = Number(item.idMuestra);
                        if (!Number.isFinite(mid) || mid <= 0) return;
                        setSelectedProductForMuestra(null);
                        setNewMuestraDraft(null);
                        setPendingMeetingMedia(null);
                        setMuestraEditMode(true);
                        getMuestraById(mid)
                          .then((data) => {
                            setNewMuestraDraft(data);
                            setMuestraDrawerOpen(true);
                          })
                          .catch(() => {
                            setError(t("Meetings.linkMuestraError"));
                          });
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            </Collapse>
          </Stack>
        </Box>
      </Paper>

      {error ? (
        <Alert
          severity="error"
          sx={{ mb: meetingSectionGap }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      ) : null}

      {!error && session.summary?.errorMessage ? (
        <Alert severity="error" sx={{ mb: meetingSectionGap }}>
          {session.summary.errorMessage}
        </Alert>
      ) : null}

      {!error && session.status === "failed" ? (
        <Alert severity="warning" sx={{ mb: meetingSectionGap }}>
          {t("Meetings.summarizeError")}
        </Alert>
      ) : null}

      {mediaWarning ? (
        <Alert
          severity="warning"
          sx={{ mb: meetingSectionGap }}
          onClose={() => setMediaWarning("")}
        >
          {mediaWarning}
        </Alert>
      ) : null}

      {mediaUploadingCount > 0 ? (
        <Alert severity="info" sx={{ mb: meetingSectionGap }} icon={false}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={16} />
            <span>
              {t("Meetings.mediaUploadingBanner", {
                count: mediaUploadingCount,
              })}
            </span>
          </Stack>
        </Alert>
      ) : null}

      {needsConsent ? (
        <Paper
          sx={{ ...pageContentCardSx(theme), mb: meetingSectionGap }}
          elevation={0}
        >
          <MeetingConsentPanel
            value={consentDraft}
            onChange={setConsentDraft}
            disabled={savingConsent}
          />
          <Button
            fullWidth
            variant="contained"
            sx={{ mt: 1.5, minHeight: 52 }}
            disabled={savingConsent || !isMeetingConsentComplete(consentDraft)}
            onClick={handleSaveConsent}
          >
            {savingConsent ? (
              <CircularProgress size={18} />
            ) : (
              t("Meetings.consentSave")
            )}
          </Button>
        </Paper>
      ) : (
        <Typography
          variant="caption"
          color="success.main"
          display="block"
          sx={{ mb: meetingSectionGap, px: 0.25, lineHeight: 1.4 }}
        >
          {t("Meetings.consentRecorded", {
            user: session.consentByUsername || "—",
            date: session.consentAt
              ? new Date(session.consentAt).toLocaleString()
              : "—",
          })}
        </Typography>
      )}

      <Box sx={meetingDesktopSplitSx}>
        <Box sx={meetingDesktopAsideSx}>
          <Paper sx={meetingCaptureCardSx(theme)} elevation={0}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                fontVariantNumeric: "tabular-nums",
                mb: 0.25,
                fontSize: { xs: "2.25rem", sm: "2.75rem", md: "2.5rem" },
                textAlign: "center",
              }}
            >
              {timerLabel}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mb: 1, textAlign: "center", px: 0.5 }}
            >
              {timerHint}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1.5, textAlign: "center" }}
            >
              {t("Meetings.counts", {
                audio: session.audioCount || 0,
                photos: session.imageCount ?? session.photoCount ?? 0,
                videos: session.videoCount || 0,
              })}
              {recorder.pendingUploads
                ? ` · ${t("Meetings.uploading", {
                    count: recorder.pendingUploads,
                  })}`
                : ""}
            </Typography>

            {recorder.isRecording ? (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 1.25, textAlign: "center", px: 0.5 }}
              >
                {t("Meetings.keepAwake")}
              </Typography>
            ) : null}

            <Stack spacing={1}>
              <Stack direction="row" spacing={1}>
                {recorder.isRecording ? (
                  <Button
                    variant="contained"
                    color="error"
                    size="large"
                    startIcon={<StopCircleOutlinedIcon />}
                    onClick={handleStopAudio}
                    disabled={busy}
                    sx={{ ...meetingActionBtnSx, flex: 1, width: "auto" }}
                  >
                    {t("Meetings.stop")}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    size="large"
                    color="warning"
                    startIcon={<MicNoneOutlinedIcon />}
                    onClick={handleStart}
                    disabled={!canRecord || busy}
                    sx={{ ...meetingActionBtnSx, flex: 1, width: "auto" }}
                  >
                    {t("Meetings.record")}
                  </Button>
                )}
                {canEndMeeting ? (
                  <Button
                    variant="outlined"
                    color="warning"
                    size="large"
                    startIcon={<FlagOutlinedIcon />}
                    onClick={handleEndMeeting}
                    disabled={busy}
                    sx={{ ...meetingActionBtnSx, flex: 1, width: "auto" }}
                  >
                    {t("Meetings.endMeeting")}
                  </Button>
                ) : null}
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<PhotoCameraOutlinedIcon />}
                  onClick={openPhotoPicker}
                  disabled={!canTakePhoto}
                  sx={{ ...meetingActionBtnSx, flex: 1, width: "auto" }}
                >
                  {t("Meetings.photo")}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<VideocamOutlinedIcon />}
                  onClick={openVideoPicker}
                  disabled={!canTakePhoto}
                  sx={{ ...meetingActionBtnSx, flex: 1, width: "auto" }}
                >
                  {t("Meetings.video")}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<CollectionsOutlinedIcon />}
                  onClick={openGalleryPicker}
                  disabled={!canTakePhoto}
                  sx={{ ...meetingActionBtnSx, flex: 1, width: "auto" }}
                >
                  {t("Meetings.fromGallery")}
                </Button>
              </Stack>
            </Stack>
            {recorder.isPausedForMedia ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1, textAlign: "center" }}
              >
                {t("Meetings.videoPausesAudio")}
              </Typography>
            ) : null}
            {recordingHeldByOther ? (
              <Alert severity="info" sx={{ mt: 1.25, py: 0.5 }}>
                {t("Meetings.recordingByOther", {
                  name:
                    session.recordingByUsername ||
                    t("Meetings.freeNotesAnonymous"),
                })}
              </Alert>
            ) : null}
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              capture={isNarrow ? "environment" : undefined}
              hidden
              onChange={handleMediaFile}
            />
            <input
              ref={videoInputRef}
              type="file"
              accept="video/*"
              multiple
              capture={isNarrow ? "environment" : undefined}
              hidden
              onChange={handleMediaFile}
            />
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              hidden
              onChange={handleMediaFile}
            />
          </Paper>

          <MeetingFreeNotesPanel
            notes={session.freeNotes || []}
            currentUserId={currentUserId}
            canModerate={canDelete}
            saving={noteSaving}
            onCreate={handleCreateFreeNote}
            onUpdate={handleUpdateFreeNote}
            onDelete={handleDeleteFreeNote}
            dense
          />

          <Paper sx={{ ...pageContentCardSx(theme), p: 1.5 }} elevation={0}>
            {summarizing ? (
              <Alert
                severity="info"
                icon={<CircularProgress size={18} />}
                sx={{ mb: 1.5 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {t("Meetings.summarizingTitle")}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.4 }}>
                  {t("Meetings.summarizingHint")}
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 0.75 }}>
                  {t("Meetings.summarizingElapsed", {
                    time: formatElapsed(summarizeElapsedSec * 1000),
                  })}
                </Typography>
                {(session.photoCount || 0) + (session.videoCount || 0) > 20 ? (
                  <Typography
                    variant="caption"
                    display="block"
                    sx={{ mt: 0.5 }}
                  >
                    {t("Meetings.summarizeHeavyHint")}
                  </Typography>
                ) : null}
              </Alert>
            ) : null}
            <Button
              fullWidth
              variant="contained"
              color={summaryDone ? "secondary" : "primary"}
              sx={{ minHeight: 52 }}
              startIcon={
                summarizing ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <AutoAwesomeOutlinedIcon />
                )
              }
              onClick={requestSummarize}
              disabled={!canSummarizeNow || busy || summarizing}
            >
              {summarizing
                ? t("Meetings.summarizingButton")
                : summaryDone
                  ? t("Meetings.resummarize")
                  : t("Meetings.summarize")}
            </Button>
            {!canSummarizePermission ? (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 1, lineHeight: 1.4 }}
              >
                {t("Meetings.summarizeOwnerOnlyHint")}
              </Typography>
            ) : null}
            {!summarizing &&
            canSummarizePermission &&
            (session.photoCount || 0) + (session.videoCount || 0) > 20 ? (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 1, lineHeight: 1.4 }}
              >
                {t("Meetings.summarizeHeavyHint")}
              </Typography>
            ) : null}
          </Paper>

          <Paper sx={{ ...pageContentCardSx(theme), p: 0 }} elevation={0}>
            <Accordion
              disableGutters
              elevation={0}
              defaultExpanded={!summaryDone}
              sx={{
                bgcolor: "transparent",
                "&:before": { display: "none" },
                boxShadow: "none",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreOutlinedIcon />}
                sx={{ px: 1.5, minHeight: 52 }}
              >
                <Typography sx={{ fontWeight: 700 }}>
                  {t("Meetings.sessionDetails")}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1.5, pb: 2, pt: 0 }}>
                <Stack spacing={2} sx={{ minWidth: 0 }}>
                  <MeetingBrandSelect
                    value={customerDraft}
                    onChange={(name) => {
                      setCustomerDraft(name);
                      setClientContactIds([]);
                      if (name !== (session.customerName || "")) {
                        patchMeetingSession(id, {
                          customerName: name,
                          clientContactIds: [],
                        })
                          .then(setSession)
                          .catch((err) =>
                            setError(err.response?.data?.error || err.message),
                          );
                      }
                    }}
                  />
                  <TextField
                    type="date"
                    label={t("Meetings.date")}
                    fullWidth
                    value={dateDraft}
                    onChange={(e) => {
                      const next = e.target.value;
                      setDateDraft(next);
                      if (next && next !== (session.meetingDate || "")) {
                        patchMeetingSession(id, { meetingDate: next })
                          .then(setSession)
                          .catch((err) =>
                            setError(err.response?.data?.error || err.message),
                          );
                      }
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <MeetingAttendeeSelect
                    value={attendeeIds}
                    onChange={(ids) => {
                      setAttendeeIds(ids);
                      patchMeetingSession(id, { attendeeUserIds: ids })
                        .then(setSession)
                        .catch((err) =>
                          setError(err.response?.data?.error || err.message),
                        );
                    }}
                  />
                  <MeetingClientContactSelect
                    brandName={customerDraft}
                    value={clientContactIds}
                    onChange={(ids) => {
                      setClientContactIds(ids);
                      patchMeetingSession(id, { clientContactIds: ids })
                        .then(setSession)
                        .catch((err) =>
                          setError(err.response?.data?.error || err.message),
                        );
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {session.mediaPurged
                      ? t("Meetings.mediaPurgedBanner")
                      : t("Meetings.retentionSessionBanner", {
                          days: mediaDaysLeftFromSession(session) ?? "—",
                          total: MEETING_MEDIA_RETENTION_DAYS,
                          warnMb: formatMeetingMediaWarnMb(),
                          maxMb: formatMeetingMediaMaxMb(),
                        })}
                  </Typography>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Paper>

          <Box
            ref={setFloatingDockHost}
            sx={{
              position: "sticky",
              top: { xs: 108, md: 0 },
              zIndex: 24,
              minHeight: 0,
            }}
          />
        </Box>

        <Box sx={meetingDesktopMainSx}>
          {summary && summary.status !== "error" ? (
            <Paper sx={pageContentCardSx(theme)} elevation={0}>
              <MeetingSummaryPrint
                session={session}
                summary={displaySummary}
                summaryLang={summaryLang}
                onSummaryLangChange={setSummaryLang}
                hasSummaryEn={Boolean(summary?.en)}
                onOpenMedia={openMedia}
                hideActions
                compact
              />
            </Paper>
          ) : (
            <Paper sx={pageContentCardSx(theme)} elevation={0}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                {t("Meetings.summary")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {summarizing
                  ? t("Meetings.summarizingHint")
                  : t("Meetings.summaryEmptyHint")}
              </Typography>
            </Paper>
          )}

          <Paper
            sx={pageContentCardSx(theme)}
            elevation={0}
            className="meeting-no-print"
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              {t("Meetings.audioChunksTitle")}
              {` · ${audioChunks.length || session.audioCount || 0}`}
            </Typography>
            {audioChunks.length ? (
              <Stack spacing={1}>
                {audioChunks.map((chunk, idx) => (
                  <Box
                    key={chunk.id || idx}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "120px minmax(0, 1fr)",
                      },
                      gap: 1,
                      alignItems: "center",
                      p: 1,
                      borderRadius: 1.5,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {t("Meetings.audioChunkLabel", {
                        n: (chunk.seq ?? idx) + 1,
                      })}
                      {chunk.hasTranscript ? " · ✓" : ""}
                    </Typography>
                    {chunk.fileUrl ? (
                      <Box
                        component="audio"
                        controls
                        preload="metadata"
                        src={meetingMediaUrl(chunk.fileUrl)}
                        sx={{ width: "100%", maxWidth: 420, height: 36 }}
                      />
                    ) : null}
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t("Meetings.audioChunkEmpty")}
              </Typography>
            )}
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mt: 1 }}
            >
              {t("Meetings.audioInSummaryHint")}
            </Typography>
          </Paper>

          {photos.length ? (
            <Paper
              sx={pageContentCardSx(theme)}
              elevation={0}
              className="meeting-no-print"
            >
              <MeetingMediaGroupsFeed
                ref={mediaFeedRef}
                photos={photos}
                mediaGroups={session?.mediaGroups || EMPTY_MEDIA_GROUPS}
                notesById={notesById}
                audioByPhotoId={audioByPhotoId}
                summaryLang={summaryLang}
                highlightPhotoId={highlightPhotoId}
                expandedNoteIds={expandedNoteIds}
                setExpandedNoteIds={setExpandedNoteIds}
                openMedia={openMedia}
                summary={displaySummary}
                onSaveGroups={handleSaveMediaGroups}
                savingGroups={savingMediaGroups}
                onSaveTeamNote={handleSaveTeamNote}
                onDeleteMedia={handleDeleteMedia}
                onCreateSample={handleCreateSampleFromMedia}
                onLinkSample={handleLinkSampleFromMedia}
                onOpenLinkedSample={openLinkedMuestra}
                onSelectionChange={setMediaSelectedCount}
                floatingDockHost={floatingDockHost}
              />
            </Paper>
          ) : (
            <Paper sx={pageContentCardSx(theme)} elevation={0}>
              <Typography color="text.secondary">
                {t("Meetings.mediaGallery")} — 0
              </Typography>
            </Paper>
          )}
        </Box>
      </Box>

      <Dialog
        open={captionOpen}
        onClose={() => setCaptionOpen(false)}
        fullWidth
        fullScreen={isNarrow}
        maxWidth="sm"
      >
        <DialogTitle>{t("Meetings.teamNote")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            fullWidth
            multiline
            minRows={3}
            maxRows={8}
            label={t("Meetings.teamNote")}
            placeholder={t("Meetings.teamNoteHint")}
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
          />
        </DialogContent>
        <DialogActions
          sx={{
            px: 2,
            py: 1.5,
            flexDirection: { xs: "column-reverse", sm: "row" },
            gap: 1,
            "& > :not(style)": { m: 0 },
          }}
        >
          <Button fullWidth={isNarrow} onClick={() => setCaptionOpen(false)}>
            {t("Meetings.skip")}
          </Button>
          <Button
            fullWidth={isNarrow}
            variant="contained"
            onClick={saveCaption}
            sx={{ minHeight: 48 }}
          >
            {t("Meetings.saveNote")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={previewOpen && Boolean(previewPhoto)}
        onClose={closePreview}
        fullWidth
        fullScreen={isNarrow}
        maxWidth="md"
        className="meeting-no-print"
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: "blur(10px)",
              backgroundColor: "rgba(15, 23, 42, 0.5)",
            },
          },
        }}
        BackdropProps={{
          sx: {
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(15, 23, 42, 0.5)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            pr: 1,
          }}
        >
          <Typography variant="h6" component="span" sx={{ fontWeight: 800 }}>
            {previewIsVideo ? t("Meetings.video") : t("Meetings.photo")} #
            {previewPhoto?.id}
            {previewPhoto?.offsetLabel ? ` · ${previewPhoto.offsetLabel}` : ""}
          </Typography>
          <IconButton aria-label={t("Meetings.cancel")} onClick={closePreview}>
            <CloseOutlinedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          {previewPhoto ? (
            previewIsVideo ? (
              <Box
                component="video"
                key={previewPhoto.id}
                src={meetingMediaUrl(previewPhoto.fileUrl)}
                controls
                autoPlay
                playsInline
                sx={{
                  width: "100%",
                  maxHeight: isNarrow ? "70vh" : "72vh",
                  bgcolor: "black",
                  borderRadius: 1.5,
                  display: "block",
                }}
              />
            ) : (
              <Box
                component="img"
                src={meetingMediaUrl(previewPhoto.fileUrl)}
                alt={previewPhoto.caption || previewPhoto.teamNote || ""}
                sx={{
                  width: "100%",
                  maxHeight: isNarrow ? "70vh" : "72vh",
                  objectFit: "contain",
                  borderRadius: 1.5,
                  display: "block",
                  bgcolor: "action.hover",
                }}
              />
            )
          ) : null}
          {previewPhoto?.teamNote ? (
            <Typography
              variant="body2"
              sx={{ mt: 1.5, whiteSpace: "pre-wrap" }}
            >
              {previewPhoto.teamNote}
            </Typography>
          ) : previewPhoto?.caption ? (
            <Typography variant="body2" sx={{ mt: 1.5 }}>
              {previewPhoto.caption}
            </Typography>
          ) : null}
        </DialogContent>
      </Dialog>

      <MeetingMediaGalleryDialog
        open={galleryOpen}
        onClose={closeGallery}
        photos={photos}
        onJumpToPhoto={jumpToPhotoInMeeting}
      />

      <MeetingSummarizeConfirmDialog
        open={summarizeConfirmOpen}
        onClose={() => setSummarizeConfirmOpen(false)}
        onConfirm={handleSummarize}
        regenerating={summaryDone}
        audioCount={session?.audioCount || 0}
        photoCount={session?.imageCount ?? session?.photoCount ?? 0}
        videoCount={session?.videoCount || 0}
        fullScreen={isNarrow}
      />

      <MeetingMoveMediaDialog
        open={moveMediaOpen}
        onClose={() => !movingMedia && setMoveMediaOpen(false)}
        currentSessionId={id}
        selectedCount={mediaSelectedCount}
        moving={movingMedia}
        onConfirm={handleConfirmMoveMedia}
      />

      <MeetingDeleteDialog
        open={deleteOpen}
        session={session}
        deleting={deleting}
        onClose={() => !deleting && setDeleteOpen(false)}
        onConfirm={handleDelete}
      />

      <Dialog
        open={leaveUploadOpen}
        onClose={() => !leavingWait && setLeaveUploadOpen(false)}
        fullWidth
        maxWidth="xs"
        fullScreen={isNarrow}
      >
        <DialogTitle>{t("Meetings.mediaUploadingLeaveTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("Meetings.mediaUploadingLeaveBody", {
              count: mediaUploadingCount || 1,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1, flexWrap: "wrap" }}>
          <Button onClick={leaveAnyway} disabled={leavingWait} color="inherit">
            {t("Meetings.mediaLeaveAnyway")}
          </Button>
          <Button
            onClick={leaveAfterUploads}
            variant="contained"
            disabled={leavingWait}
            startIcon={
              leavingWait ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {leavingWait
              ? t("Meetings.mediaUploadingWait")
              : t("Meetings.mediaWaitUpload")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={needGroupWarnOpen}
        onClose={closeNeedGroupWarn}
        fullWidth
        maxWidth="xs"
        fullScreen={isNarrow}
        BackdropProps={{
          sx: {
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(15, 23, 42, 0.45)",
          },
        }}
      >
        <DialogTitle>{t("Meetings.createSampleNeedGroupTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("Meetings.createSampleNeedGroupBody")}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            fullWidth={isNarrow}
            variant="contained"
            onClick={closeNeedGroupWarn}
            sx={{ minHeight: 48 }}
          >
            {t("Meetings.createSampleNeedGroupOk")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={emptyTeamNoteWarnOpen}
        onClose={cancelCreateSampleEmptyNotes}
        fullWidth
        maxWidth="xs"
        fullScreen={isNarrow}
        BackdropProps={{
          sx: {
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(15, 23, 42, 0.45)",
          },
        }}
      >
        <DialogTitle>{t("Meetings.createSampleEmptyNotesTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>
            {t("Meetings.createSampleEmptyNotesBody", {
              count: emptyTeamNotePhotoIds.length,
              total: pendingSamplePhotos.length,
            })}
          </DialogContentText>
          {emptyTeamNotePhotoIds.length ? (
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: "text.secondary" }}
            >
              {t("Meetings.createSampleEmptyNotesIds", {
                ids: emptyTeamNotePhotoIds.map((id) => `#${id}`).join(", "),
              })}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions
          sx={{
            px: 2,
            pb: 2,
            flexDirection: { xs: "column-reverse", sm: "row" },
            gap: 1,
            "& > :not(style)": { m: 0 },
          }}
        >
          <Button fullWidth={isNarrow} onClick={cancelCreateSampleEmptyNotes}>
            {t("Meetings.createSampleEmptyNotesBack")}
          </Button>
          <Button
            fullWidth={isNarrow}
            variant="contained"
            color="warning"
            onClick={continueCreateSampleWithoutNotes}
            sx={{ minHeight: 48 }}
          >
            {t("Meetings.createSampleEmptyNotesContinue")}
          </Button>
        </DialogActions>
      </Dialog>

      <MeetingCreateSampleDialog
        open={productGateOpen}
        onClose={() => setProductGateOpen(false)}
        onProductChosen={handleProductChosenForSample}
        mediaCount={pendingSamplePhotos.length}
      />

      <MeetingCreateMoodboardDialog
        open={moodboardOpen}
        onClose={() => {
          setMoodboardOpen(false);
          setMoodboardPhotos([]);
        }}
        session={session}
        selectedPhotos={moodboardPhotos}
        onCreated={(created) => {
          if (created?.id) {
            navigate(`/skills/tareas?moodboard=${created.id}`);
          }
        }}
      />

      <MeetingLinkMuestraDialog
        open={linkMuestraOpen}
        onClose={() => {
          if (linkingMuestra) return;
          setLinkMuestraOpen(false);
          setPendingSamplePhotos([]);
        }}
        onConfirmMuestra={confirmLinkExistingMuestra}
        onConfirmProduct={async (product) => {
          setLinkMuestraOpen(false);
          await handleProductChosenForSample(product);
        }}
        mediaCount={pendingSamplePhotos.length}
        linking={linkingMuestra}
        fullScreen={isNarrow}
      />

      <MeetingLeaveSamplesDialog
        open={leaveSamplesOpen}
        onClose={() => setLeaveSamplesOpen(false)}
        session={session}
        onDone={handleLeaveSamplesDone}
      />

      {newMuestraDraft && muestraCatalogs ? (
        <DrawerMuestraV2
          open={muestraDrawerOpen}
          onClose={closeMuestraDrawer}
          isEditMode={muestraEditMode}
          newMuestra={newMuestraDraft}
          selectedProduct={selectedProductForMuestra}
          tiposMuestra={muestraCatalogs.tiposMuestra}
          tiposEstado={muestraCatalogs.tiposEstado}
          tiposTallas={muestraCatalogs.tiposTallas}
          tiposColores={muestraCatalogs.tiposColores}
          tiposProveedores={muestraCatalogs.tiposProveedores}
          tiposMarcas={muestraCatalogs.tiposMarcas}
          escandalloGrupos={muestraCatalogs.escandalloGrupos}
          escandallosIniciales={[]}
          userCanCreateSample={userCanCreateSample}
          pendingMeetingMedia={pendingMeetingMedia}
          orderEmailAttachMode="fichaPdf"
          keepOpenAfterSave
          onDeleteMuestra={handleDeleteMuestraFromMeeting}
          onMeetingMediaImported={(result) => {
            setPendingMeetingMedia(null);
            applyImportedMeetingLinks(result);
          }}
          onMuestraPersisted={handleMuestraPersistedFromMeeting}
        />
      ) : null}
    </Box>
  );
}
